import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card } from "../../components/common/Primitives";
import api from "../../lib/api";
import { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useProperty } from "../../lib/PropertyContext";
import { toast, Toaster } from "sonner";
import {
  Landmark, Inbox, Handshake, IndianRupee, ArrowRight, RefreshCw,
  TrendingUp, Users, ChevronRight, Clock,
} from "lucide-react";

/**
 * MoneyDashboard — property-scoped landing page for VenturaZ Money.
 *
 * When Super Admin switches the property switcher to VenturaZ Money the
 * sidebar Overview link brings them here (not to the debt Kanban). This
 * page rolls up lead intake, active pipeline value, disbursed volume and
 * partner-app queue in one screen so ops can triage without hunting.
 *
 * Uses maroon/copper accents to visually distinguish from the equity
 * console.
 */

const fmtCrore = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `\u20B9${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `\u20B9${(v / 1e5).toFixed(2)} L`;
  return `\u20B9${v.toLocaleString("en-IN")}`;
};
const fmtDT = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16).replace("T", " "); }
};

export default function MoneyDashboard() {
  const { user } = useAuth();
  const { activeProperty } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isMultiPropAdmin = ["super_admin", "admin", "manager", "agent"].includes(user?.role);
  const shouldRedirect = isMultiPropAdmin && activeProperty?.slug === "venturaz";

  const load = () => {
    setLoading(true);
    api.get("/admin/money/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (shouldRedirect) return;
    load();
  }, [shouldRedirect]);

  // Symmetric redirect: multi-property admin switching back to Equity leaves
  // the Money Dashboard for the equity console.
  if (shouldRedirect) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const leads = data?.leads || {};
  const cases = data?.cases || {};
  const partners = data?.partners || {};

  return (
    <DashboardShell title="VenturaZ Money · Console" subtitle="ADMIN · DEBT OVERVIEW">
      <Toaster position="top-right" richColors />
      {/* Header row */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.32em] text-[#B8860B] uppercase mb-1">Property · VenturaZ Money</div>
          <p className="text-sm text-ink-muted max-w-xl">
            Real-time snapshot of debt intake, pipeline value and partner applications. Updated when the page loads — hit refresh to re-fetch.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-[#B8860B]/40 text-[#8A6600] hover:bg-[#B8860B]/[0.08] transition-colors rounded-md"
          data-testid="money-dashboard-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MoneyTile
          label="Total Leads"
          value={leads.total ?? 0}
          sub={`${leads.new_7d ?? 0} in last 7d`}
          icon={Inbox}
          testId="money-tile-total-leads"
          to="/admin/debt/leads"
        />
        <MoneyTile
          label="Pipeline Value"
          value={fmtCrore(cases.pipeline_value_inr)}
          sub={`${cases.active ?? 0} active cases`}
          icon={TrendingUp}
          testId="money-tile-pipeline"
          to="/admin/debt"
        />
        <MoneyTile
          label="Disbursed"
          value={fmtCrore(cases.disbursed_value_inr)}
          sub={`${cases.disbursed_count ?? 0} cases closed won`}
          icon={IndianRupee}
          testId="money-tile-disbursed"
          to="/admin/debt"
        />
        <MoneyTile
          label="Partner Apps"
          value={partners.pending ?? 0}
          sub={`${partners.total ?? 0} lifetime`}
          icon={Handshake}
          testId="money-tile-partner-apps"
          to="/admin/channel-partners"
          highlight={(partners.pending ?? 0) > 0}
        />
      </div>

      {/* Two column: Recent Leads + Pipeline breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card testId="money-recent-leads" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline mb-1">Recent Leads</div>
              <div className="text-xs text-ink-muted">Latest submissions from the debt apply wizard · partner tiles · hero forms.</div>
            </div>
            <Link
              to="/admin/debt/leads"
              className="text-[11px] font-mono uppercase tracking-widest text-[#8A6600] hover:text-[#5A4400] flex items-center gap-1"
              data-testid="money-recent-leads-viewall"
            >
              View all <ChevronRight size={13} />
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-ink-muted border-b border-white/10">
                <th className="py-2">When</th>
                <th className="py-2">Contact</th>
                <th className="py-2">Product</th>
                <th className="py-2 text-right">Ask</th>
                <th className="py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {(leads.recent || []).map((l) => (
                <tr key={l.lead_id} className="border-b border-white/5 hover:bg-[#B8860B]/[0.04]" data-testid={`money-lead-row-${l.lead_id}`}>
                  <td className="py-2 text-ink-muted font-mono text-[11px] whitespace-nowrap">
                    <Clock size={10} className="inline mr-1 text-[#B8860B]" />{fmtDT(l.created_at)}
                  </td>
                  <td className="py-2 text-ink">
                    <div className="truncate max-w-[180px]" title={l.contact_name}>{l.contact_name || "\u2014"}</div>
                    <div className="text-[10px] text-ink-muted font-mono">{l.contact_phone || l.contact_email || ""}</div>
                  </td>
                  <td className="py-2 text-ink-soft text-xs capitalize">{(l.product || "").replace(/_/g, " ")}</td>
                  <td className="py-2 text-right text-ink font-mono text-xs">{fmtCrore(l.amount_inr)}</td>
                  <td className="py-2 text-[10px] font-mono text-ink-muted truncate max-w-[140px]" title={l.source_section}>
                    {l.source_page?.replace("/raise-debt/", "") || "-"}
                  </td>
                </tr>
              ))}
              {!loading && (leads.recent || []).length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-ink-muted text-xs">No leads yet.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={5} className="text-center py-6 text-ink-muted text-xs">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        {/* Partner apps panel */}
        <Card testId="money-partner-apps">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline mb-1">Partner Applications</div>
              <div className="text-xs text-ink-muted">DSA / channel-partner sign-ups awaiting review.</div>
            </div>
            <Link
              to="/admin/channel-partners"
              className="text-[11px] font-mono uppercase tracking-widest text-[#8A6600] hover:text-[#5A4400] flex items-center gap-1"
              data-testid="money-partner-apps-viewall"
            >
              Review <ChevronRight size={13} />
            </Link>
          </div>
          <ul className="space-y-2" data-testid="money-partner-apps-list">
            {(partners.recent || []).slice(0, 5).map((p) => (
              <li key={p.application_id} className="border border-white/10 bg-white/50 backdrop-blur px-3 py-2 rounded flex items-start gap-2">
                <Handshake size={14} className="text-[#B8860B] mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink font-heading truncate">{p.name || p.contact_name || "Unnamed"}</div>
                  <div className="text-[11px] text-ink-muted font-mono truncate">{p.mobile || p.email || ""}</div>
                  <div className="text-[10px] text-ink-faint font-mono mt-0.5">
                    {(p.city || "-")}{p.state ? ` · ${p.state}` : ""} · {fmtDT(p.created_at)}
                  </div>
                </div>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded self-start ${
                  p.status === "approved" ? "bg-emerald-500/15 text-emerald-800" :
                  p.status === "rejected" ? "bg-rose-500/15 text-rose-800" :
                  "bg-[#B8860B]/15 text-[#8A6600]"
                }`}>
                  {p.status || "new"}
                </span>
              </li>
            ))}
            {!loading && (partners.recent || []).length === 0 && (
              <li className="text-center py-6 text-ink-muted text-xs">No partner applications yet.</li>
            )}
          </ul>
        </Card>
      </div>

      {/* Stage-count strip */}
      <Card testId="money-stage-strip">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline mb-1">Pipeline by Stage</div>
            <div className="text-xs text-ink-muted">Case count by canonical debt stage. Click through to the Kanban to advance.</div>
          </div>
          <Link
            to="/admin/debt"
            className="text-[11px] font-mono uppercase tracking-widest text-[#8A6600] hover:text-[#5A4400] flex items-center gap-1"
            data-testid="money-stage-viewboard"
          >
            Open Kanban <ArrowRight size={13} />
          </Link>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {(cases.pipeline || []).map((stage) => {
            const n = (cases.stage_counts || {})[stage] || 0;
            return (
              <div
                key={stage}
                className="flex-shrink-0 min-w-[140px] border border-[#B8860B]/30 bg-white/60 backdrop-blur px-3 py-2 rounded"
                data-testid={`money-stage-${stage.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="text-[9px] font-mono uppercase tracking-widest text-[#8A6600] truncate" title={stage}>{stage}</div>
                <div className="font-heading text-xl text-ink mt-1">{n}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Quick jumps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <MoneyQuickCard to="/admin/debt/leads" title="Triage New Leads" count={leads.unassigned} sub="unassigned" icon={Inbox} />
        <MoneyQuickCard to="/admin/debt" title="Advance Live Cases" count={cases.active} sub="active pipeline" icon={Landmark} />
        <MoneyQuickCard to="/admin/channel-partners" title="Review Partner Sign-ups" count={partners.pending} sub="pending review" icon={Users} />
      </div>
    </DashboardShell>
  );
}

function MoneyTile({ label, value, sub, icon: Icon, testId, to, highlight }) {
  const body = (
    <>
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#8A6600]">{label}</div>
        <Icon size={16} className="text-[#B8860B]" />
      </div>
      <div className="font-heading text-2xl text-ink" data-testid={`${testId}-value`}>{value}</div>
      {sub ? <div className="text-xs text-ink-muted mt-1 font-mono">{sub}</div> : null}
    </>
  );
  const base = "glass rounded-lg p-5 card-hover transition-all block border";
  const cls = highlight
    ? `${base} border-[#B8860B]/60 shadow-[inset_0_0_20px_rgba(184,134,11,0.10)] hover:border-[#B8860B]`
    : `${base} border-white/10 hover:border-[#B8860B]/50 hover:ring-2 hover:ring-[#B8860B]/25`;
  return to ? (
    <Link to={to} className={cls} data-testid={testId}>{body}</Link>
  ) : (
    <div className={cls} data-testid={testId}>{body}</div>
  );
}

function MoneyQuickCard({ to, title, count, sub, icon: Icon }) {
  return (
    <Link
      to={to}
      className="border border-[#B8860B]/25 bg-white/70 backdrop-blur p-5 hover:border-[#B8860B]/60 hover:bg-white/85 transition-all rounded-lg block"
      data-testid={`money-quickcard-${to.replace(/\W+/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <Icon className="text-[#B8860B]" size={16} />
          <div className="font-heading text-ink text-lg mt-3">{title}</div>
          <div className="text-sm text-ink-muted mt-1"><span className="font-mono text-ink">{count ?? 0}</span> {sub}</div>
        </div>
        <ArrowRight className="text-[#B8860B]" size={18} />
      </div>
    </Link>
  );
}
